// Copyright ©️ 2026 eVolve MEP, LLC

import {
  today,
  type CustomProperty,
  type WorkOrder,
  type WorkOrderPaginated,
  type WorkOrderAggregatedBOMItem,
  type Spool,
} from '@evolvemep/api-ts';
import { Flex } from '@mantine/core';
import type { ColDef, ValueGetterParams } from 'ag-grid-enterprise';

import { EvolveIcon } from 'assets/icons/EvolveIcon';
import { AgGridStyleTooltip } from 'components/Mantine/AgGridStyleTooltip';
import { AttachmentsPopover } from 'components/Mantine/Attachments/AttachmentsPopover';
import { CellRenderer } from 'components/Mantine/CellRenderer';
import { hoursColDef } from 'components/Mantine/commonColumnDefs';
import { getCustomPropertyColDefs } from 'components/Mantine/CustomProperties/getCustomPropertyColDefs';
import { DatePickerWrapped } from 'components/Mantine/DateInputWrapped';
import { convertSecondsToDuration } from 'components/Mantine/durationHelpers';
import { WORK_ORDER_LABEL_TYPE_ID } from 'components/Mantine/Labels/constants';
import { LabelBadges } from 'components/Mantine/Labels/LabelBadges';
import { ManageLabelsModal } from 'components/Mantine/Labels/ManageLabelsModal/ManageLabelsModal';
import { MultiUserSelect } from 'components/Mantine/MultiUserSelect';
import { EvolveLink } from 'components/Mantine/Navigation/EvolveLink';
import { TextareaWrapped } from 'components/Mantine/TextInputWrapped';
import { UserInfoBadge, UserInfoList } from 'components/Mantine/UserInfoBadge';
import { lateColor } from 'constants/badgeMappingStatus';
import { lockedColDef } from 'helpers/ag-grid/baseColumnDef';
import { editableColDef } from 'helpers/ag-grid/EditableCells/editableColDef';
import { EntitySetFilterComponent } from 'helpers/ag-grid/EntitySetFilterComponent';
import { getOnSiteDateColDef, getScheduleColDefs } from 'helpers/ag-grid/getScheduleColDefs';
import { refreshGrid } from 'helpers/ag-grid/helpers';
import { formatQuantity } from 'helpers/formatQuantity';
import { isNotNil } from 'helpers/isNotNil';
import { validateUserNameOrEmail } from 'helpers/userHelpers';
import { EditableWorkOrderStatusBadge } from 'modules/Shop/WorkOrders/WorkOrderDetail/EditableWorkOrderStatusBadge';
import type { WorkOrderAction } from 'modules/Shop/WorkOrders/WorkOrdersPage/Actions/useCanEditWorkOrder';
import { WorkOrderActionButtons } from 'modules/Shop/WorkOrders/WorkOrdersPage/Actions/WorkOrderActionButtons';
import { ActionWorkOrderMenu } from 'modules/Shop/WorkOrders/WorkOrdersPage/ActionWorkOrderMenu';
import { BOMItemIcon } from 'modules/Shop/WorkOrders/WorkOrdersPage/BOMItemIcon';
import type { WorkOrdersPageAgContext } from 'modules/Shop/WorkOrders/WorkOrdersPage/common';
import { WorkOrdersLabelChecklist } from 'modules/Shop/WorkOrders/WorkOrdersPage/WorkOrdersLabelChecklist';
import { WorkOrdersLabelFilter } from 'modules/Shop/WorkOrders/WorkOrdersPage/WorkOrdersLabelFilter';
import { convertMantineColorToCss } from 'styles/theme/useGenerateMantineTheme';

export const getWorkOrderColDef = (
  hasModifyWorkOrderAccess: boolean,
  canEditWorkOrder: (action: WorkOrderAction, workOrder: WorkOrderPaginated) => boolean,
  isActive: boolean,
  customWorkOrderProperties: CustomProperty[],
): ColDef<WorkOrderPaginated>[] => [
  {
    ...lockedColDef('left', true),
    cellRenderer: 'agGroupCellRenderer',
    width: 48,
  },
  {
    field: 'identifier',
    headerName: 'Work Order Id',
    tooltipField: 'identifier',
    lockVisible: true,
    pinned: true,
    cellRenderer: CellRenderer<WorkOrderPaginated>(({ value, data }) => (
      <EvolveLink to={`/work-orders/${data.workOrderId}`} from="Work Orders">
        <Flex align="center" gap="xs">
          <EvolveIcon icon="WorkOrder" light />
          {value}
        </Flex>
      </EvolveLink>
    )),
    filter: 'agTextColumnFilter',
    filterParams: {
      filterOptions: ['contains'],
      maxNumConditions: 1,
      buttons: ['reset'],
    },
  },
  {
    field: 'name',
    headerName: 'Work Order Name',
    tooltipField: 'name',
    flex: 1,
    minWidth: 220,
    filter: 'agTextColumnFilter',
    filterParams: {
      filterOptions: ['contains'],
      maxNumConditions: 1,
      buttons: ['reset'],
    },
  },
  {
    field: 'facilityName',
    tooltipField: 'facilityName',
    headerName: 'Facility',
  },
  {
    field: 'projectName',
    tooltipField: 'projectName',
    headerName: 'Project',
  },
  {
    field: 'workOrderStateName',
    headerName: 'Status',
    minWidth: 140,
    width: 200,
    valueGetter: ({ data, context }: ValueGetterParams<WorkOrderPaginated, unknown, WorkOrdersPageAgContext>) =>
      isNotNil(data)
        ? (context.statuses.find((s) => s.statusId === data.workOrderStatusId)?.name ??
          context.stateLabelMap[data.workOrderStateName].label)
        : null,
    cellRenderer: CellRenderer<WorkOrder>(({ data, node }) => (
      <EditableWorkOrderStatusBadge
        editable
        workOrder={data}
        onWorkOrderUpdated={(updatedWorkOrder) => node.updateData(updatedWorkOrder)}
      />
    )),
    sortable: false,
    filter: EntitySetFilterComponent<WorkOrderPaginated, 'Work Order'>('workOrderStateIds'),
  },
  editableColDef({
    // Pull out the `needBy` config, so we can make it editable
    ...getScheduleColDefs<WorkOrderPaginated>({
      getIsCompleted: (wo) => wo.workOrderStateName === 'Completed',
    })[0],
    Editor: DatePickerWrapped,
    editorProps: {
      minDate: today(),
    },
    closeOnChange: true,
    editable: (wo) => canEditWorkOrder('editProperties', wo),
    dataMapper: (_, updatedNeedBy) => ({
      needByDate: updatedNeedBy,
    }),
  }),
  editableColDef({
    ...getOnSiteDateColDef<WorkOrderPaginated>({
      getOnSiteDate: (workOrder) => workOrder.onSiteDate ?? undefined,
    }),
    Editor: DatePickerWrapped,
    editorProps: {
      minDate: today(),
    },
    closeOnChange: true,
    editable: (wo) => canEditWorkOrder('editProperties', wo),
    dataMapper: (_, updatedOnSiteDate) => ({
      onSiteDate: updatedOnSiteDate,
    }),
  }),
  // Leave the remaining configs the same
  ...getScheduleColDefs<WorkOrderPaginated>({
    hideDaysEarlyLate: isActive,
    getIsCompleted: (wo) => wo.workOrderStateName === 'Completed',
  }).slice(1),
  {
    field: 'estimatedTimeInSeconds',
    headerName: 'Manual Estimated Time',
    width: 185,
    hide: !hasModifyWorkOrderAccess,
    ...hoursColDef(),
  },
  {
    field: 'totalTimeInSeconds',
    headerName: 'Total Time',
    headerTooltip: 'Does not include actively running tasks',
    width: 150,
    hide: !hasModifyWorkOrderAccess,
    ...hoursColDef(),
  },
  {
    colId: 'estimatedVsTotal',
    headerName: 'Estimated vs Total',
    headerTooltip:
      'Calculates difference between Estimated and Total time; value is displayed in red when total exceeds estimated',
    valueGetter: ({ data }) =>
      data?.totalTimeInSeconds && data.estimatedTimeInSeconds
        ? data.totalTimeInSeconds - data.estimatedTimeInSeconds
        : null,
    valueFormatter: ({ value }: { value: number | null }) => (value ? (value / 60 / 60).toFixed(2) : ''),
    cellRenderer: CellRenderer<WorkOrder, never, number | null>(({ value: estimatedVsTotal }) =>
      isNotNil(estimatedVsTotal)
        ? `${estimatedVsTotal > 0 ? '+' : '–'} ${convertSecondsToDuration(Math.abs(estimatedVsTotal))}`
        : '',
    ),
    cellStyle: ({ value: estimatedVsTotal }) =>
      isNotNil(estimatedVsTotal)
        ? {
            color: estimatedVsTotal > 0 ? convertMantineColorToCss(lateColor) : 'green',
          }
        : undefined,
    width: 180,
    sortable: false,
  },
  editableColDef({
    field: 'description',
    tooltipField: 'description',
    headerName: 'Description',
    sortable: false,
    filter: 'agTextColumnFilter',
    filterParams: {
      filterOptions: ['contains'],
      maxNumConditions: 1,
      buttons: ['reset'],
    },
    Editor: (props) => (
      <TextareaWrapped
        {...props}
        autosize
        minRows={2}
        maxRows={10}
        onFocus={(e) => e.currentTarget.setSelectionRange(0, e.currentTarget.value.length)}
        maxLength={512}
      />
    ),
    editable: (wo) => canEditWorkOrder('editProperties', wo),
  }),
  editableColDef<WorkOrderPaginated, 'labels'>({
    field: 'labels',
    colId: 'labelIds',
    headerName: 'Labels',
    minWidth: 200,
    // Used for grid export
    valueFormatter: ({ data }) => data?.labels.map((label) => label.name).join(', ') ?? '--',
    cellRenderer: CellRenderer<WorkOrder, 'labels'>(({ value }) => (
      <LabelBadges labelTypeId={WORK_ORDER_LABEL_TYPE_ID} labels={value} />
    )),
    Editor: WorkOrdersLabelChecklist,
    editable: true,
    sortable: false,
    filter: WorkOrdersLabelFilter,
    editorFooter: <ManageLabelsModal labelTypeId={WORK_ORDER_LABEL_TYPE_ID} />,
  }),
  {
    field: 'createdByUser',
    headerName: 'Created By',
    // Used for grid export
    valueFormatter: ({ value }) => validateUserNameOrEmail(value),
    cellRenderer: CellRenderer<WorkOrderPaginated, 'createdByUser'>(({ value }) => (
      <UserInfoBadge key={value.userId} user={value} />
    )),
  },
  editableColDef({
    field: 'assignees',
    headerName: 'Assignee',
    // Used for grid export
    valueFormatter: ({ data }) => data?.assignees.map((a) => validateUserNameOrEmail(a)).join(', ') ?? '--',
    cellRenderer: CellRenderer<WorkOrderPaginated, 'assignees'>(({ value }) => <UserInfoList users={value} />),
    sortable: false,
    Editor: MultiUserSelect,
    editable: (wo) => canEditWorkOrder('editProperties', wo),
    width: 270,
  }),
  ...getCustomPropertyColDefs<WorkOrderPaginated>(customWorkOrderProperties),
  {
    ...lockedColDef('right', false),
    width: 100,
    cellRenderer: CellRenderer<WorkOrderPaginated>(
      ({ data }) => (
        <AttachmentsPopover
          workOrderId={data.workOrderId}
          numberOfAttachments={data.totalAttachmentCount}
          allowUploads="workOrder"
          showWhenEmpty
        />
      ),
      {
        flexProps: { justify: 'flex-end' },
      },
    ),
  },
  {
    ...lockedColDef('right'),
    width: 120,
    cellRenderer: CellRenderer<WorkOrderPaginated>(
      ({ data, node, api }) => (
        <WorkOrderActionButtons
          workOrder={data}
          onWorkOrderUpdated={(workOrder) => node.updateData(workOrder)}
          // Actioning a Work Order is very likely to cause it to appear in a different spot in the grid,
          // so we need to refresh the entire grid
          onWorkOrderActioned={() => refreshGrid(api, node)}
        />
      ),
      { flexProps: { justify: 'flex-end' } },
    ),
  },
  {
    ...lockedColDef('right'),
    width: 50,
    cellRenderer: CellRenderer<WorkOrderPaginated>(({ data, node, api }) => (
      // Actioning a Work Order is very likely to cause it to appear in a different spot in the grid,
      // so we need to refresh the entire grid
      <ActionWorkOrderMenu
        workOrder={data}
        onWorkOrderActioned={() => refreshGrid(api, node)}
        onWorkOrderDeleted={() => refreshGrid(api, node)}
        isWorkOrdersPage
      />
    )),
  },
];

export const getWorkOrderBomColDef = (): ColDef<WorkOrderAggregatedBOMItem>[] => [
  {
    field: 'item',
    tooltipField: 'item',
    minWidth: 200,
    flex: 3,
    cellRenderer: CellRenderer<WorkOrderAggregatedBOMItem>(({ value, data }) => (
      <>
        <BOMItemIcon itemType={data.itemType} />
        {value}
      </>
    )),
  },
  {
    field: 'spoolQuantity',
    headerName: 'Spool Qty',
    minWidth: 100,
    flex: 1,
    valueFormatter: ({ value }: { value: number | undefined }) => formatQuantity(value),
    hide: true,
  },
  {
    field: 'quantityRequired',
    headerName: 'Total Qty',
    valueFormatter: ({ value }: { value: number | undefined }) => formatQuantity(value),
    minWidth: 100,
    flex: 1,
  },
  { field: 'unitOfMeasure', headerName: 'UOM', minWidth: 100, flex: 1 },
  { field: 'manufacturer', tooltipField: 'manufacturer', minWidth: 120, flex: 1 },
  {
    field: 'catalogNumber',
    tooltipField: 'catalogNumber',
    headerName: 'Catalog #',
    minWidth: 120,
    flex: 1,
  },
];

export const spoolsItemColDef: ColDef<Spool>[] = [
  {
    field: 'spool',
    minWidth: 300,
    flex: 1,
    cellRenderer: CellRenderer<Spool, 'spool'>(({ value, data }) => (
      <>
        <EvolveIcon icon="Spools" light />
        {value}
        {!data.itemCount && (
          <AgGridStyleTooltip label="This spool has no bill of materials." withArrow openDelay={0}>
            <EvolveIcon icon="Alert" color="orange.4" />
          </AgGridStyleTooltip>
        )}
      </>
    )),
  },
];
